import * as React from 'react'
import styled from 'styled-components'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import {
  CheckBox,
  DipseaContentfulAssets,
  Form,
  H160,
  H250,
  P14,
  P18
} from '@components/shared'
import { config, Container, Row, Col, Hidden } from 'react-awesome-styled-grid'
import { colors } from '@components/shared/colors'
import type { PurchaseGiftParamsType } from '../../../models/Gift'
import { isEmailValid } from '@helpers/authHelpers'
import PromoCode from '../../PromoCode'
import StoreAvailability from '@components/Availability'
import { contentfulLoader } from '@services/images'
import { FadeInImage } from '@components/shared/FadeInImage'
import { SUBSCRIPTION_TYPES } from '@helpers/promos'
import type { SubscriptionTextType, SubscriptionLengthType } from '../../../flowTypes'
import { isServer } from '../../../env'
import { ANNUAL_PRICE_STRING, SIX_MONTHS_PRICE_STRING } from '../../../helpers/subscriptionHelpers'

export type PropsType = {
  purchasingGiftErrorMessage?: string,
  purchasingGift: boolean,
  giftCode?: string,
  completeOrder: (
    params: PurchaseGiftParamsType,
    name: string,
    stripe: any,
    cardElement: any
  ) => void,
  newPrice?: string,
  promoText?: string,
  subscriptionText: SubscriptionTextType,
  featurePrideGift: boolean
}

export type DispatchPropsType = {
  setPlanLength: (planLength: 'six_months' | 'annual') => void
}

const CARD_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: colors.sand,
      color: colors.sand,
      fontWeight: 500,
      fontFamily: 'Good Sans Light, Roboto, Open Sans, Segoe UI, sans-serif',
      fontSize: '12px',
      lineHeight: '18px',
      fontSmoothing: 'antialiased',
      '::placeholder': {
        color: colors.sand,
        textTransform: 'capitalize'
      },
      ':focus': {
        color: colors.midnightBlue,
        '::placeholder': {
          color: colors.midnightBlue
        }
      }
    },
    invalid: {
      iconColor: colors.orange,
      color: colors.orange
    }
  }
} as const

function GiftCheckout (props: PropsType & DispatchPropsType): React.ReactNode {
  const stripe = useStripe()
  const elements = useElements()
  const isMailGiftOptionAvailabe = false
  const [subscriptionGiftType, setSubscriptionGiftType] = React.useState<SubscriptionLengthType>(SUBSCRIPTION_TYPES.ANNUAL)

  React.useEffect((): void => {
    if (!isServer && typeof window !== 'undefined') {
      const search = window.location?.search || ''

      if (search.includes('length=six')) {
        props.setPlanLength(SUBSCRIPTION_TYPES.SIX_MONTHS)
        return setSubscriptionGiftType(SUBSCRIPTION_TYPES.SIX_MONTHS)
      }
    }

    props.setPlanLength(SUBSCRIPTION_TYPES.ANNUAL)
    setSubscriptionGiftType(SUBSCRIPTION_TYPES.ANNUAL)
  }, [])

  const [mailGift, setMailGift] = React.useState<boolean>(false)
  const [purchaserName, setPurchaserName] = React.useState<string | null | undefined>()
  const [purchaserNameError, setPurchaserNameError] = React.useState<string | null | undefined>()
  const [purchaserEmail, setPurchaserEmail] = React.useState<string | null | undefined>()
  const [
    purchaserEmailError,
    setPurchaserEmailError
  ] = React.useState<string | null | undefined>()
  const [
    confirmPurchaserEmail,
    setConfirmPurchaserEmail
  ] = React.useState<string | null | undefined>()
  const [
    confirmPurchaserEmailError,
    setConfirmPurchaserEmailError
  ] = React.useState<string | null | undefined>()
  const [sendToSelf, setSendToSelf] = React.useState<boolean>(false)
  const [recipientEmail, setRecipientEmail] = React.useState<string | null | undefined>()
  const [
    recipientEmailError,
    setRecipientEmailError
  ] = React.useState<string | null | undefined>()
  const [
    confirmRecipientEmail,
    setConfirmRecipientEmail
  ] = React.useState<string | null | undefined>()
  const [
    confirmRecipientEmailError,
    setConfirmRecipientEmailError
  ] = React.useState<string | null | undefined>()
  const [recipientName, setRecipientName] = React.useState<string | null | undefined>()
  const [recipientNameError, setRecipientNameError] = React.useState<string | null | undefined>()
  const [address1, setAddress1] = React.useState<string | null | undefined>()
  const [address1Error, setAddress1Error] = React.useState<string | null | undefined>()
  const [address2, setAddress2] = React.useState<string | null | undefined>()
  const [city, setCity] = React.useState<string | null | undefined>()
  const [cityError, setCityError] = React.useState<string | null | undefined>()
  const [state, setState] = React.useState<string | null | undefined>()
  const [stateError, setStateError] = React.useState<string | null | undefined>()
  const [zipCode, setZipCode] = React.useState<string | null | undefined>()
  const [zipCodeError, setZipCodeError] = React.useState<string | null | undefined>()
  const [cardTo, setCardTo] = React.useState<string | null | undefined>()
  const [cardToError, setCardToError] = React.useState<string | null | undefined>()
  const [cardFrom, setCardFrom] = React.useState<string | null | undefined>()
  const [cardFromError, setCardFromError] = React.useState<string | null | undefined>()
  const [nameOnCard, setNameOnCard] = React.useState<string | null | undefined>()
  const [nameOnCardError, setNameOnCardError] = React.useState<string | null | undefined>()
  const [cardComplete, setCardComplete] = React.useState<boolean>(false)

  const params = {
    mailGift,
    purchaserName,
    purchaserNameError,
    purchaserEmail,
    purchaserEmailError,
    confirmPurchaserEmail,
    confirmPurchaserEmailError,
    sendToSelf,
    recipientEmail,
    recipientEmailError,
    confirmRecipientEmail,
    confirmRecipientEmailError,
    recipientName,
    recipientNameError,
    address1,
    address1Error,
    address2,
    city,
    cityError,
    state,
    stateError,
    zipCode,
    zipCodeError,
    cardTo,
    cardToError,
    cardFrom,
    cardFromError,
    nameOnCard
  }

  const orderParams = {
    mailGift,
    purchaserName,
    purchaserEmail,
    confirmPurchaserEmail,
    sendToSelf,
    recipientEmail,
    confirmRecipientEmail,
    recipientName,
    address1,
    address2,
    city,
    state,
    zipCode,
    cardTo,
    cardFrom,
    nameOnCard,
    subscriptionGiftType
  }

  const completeOrder = (e: any): void => {
    e.preventDefault()
    const element = elements?.getElement(CardElement)
    if (!element) {
      console.error('Card element not available')
      return
    }
    const definedParams: Record<string, any> = {}
    const allParams = orderParams as Record<string, any>
    Object.keys(allParams).forEach((key: string): void => {
      if (allParams[key] !== undefined) {
        definedParams[key] = allParams[key]
      }
    })
    if (nameOnCard) {
      props.completeOrder(definedParams as PurchaseGiftParamsType, nameOnCard, stripe, element)
    }
  }

  const fieldNotBlank = (field?: any): boolean => {
    return !!field && field.length > 0
  }

  const noError = (field?: string | null): boolean => {
    return !field
  }

  const orderComplete = (params: any): boolean => {
    if (!params.mailGift && !params.sendToSelf) {
      return (
        fieldNotBlank(params.purchaserName) &&
        noError(params.purchaserNameError) &&
        fieldNotBlank(params.purchaserEmail) &&
        noError(params.purchaserEmailError) &&
        fieldNotBlank(params.confirmPurchaserEmail) &&
        noError(params.confirmPurchaserEmailError) &&
        fieldNotBlank(params.recipientName) &&
        noError(params.recipientNameError) &&
        fieldNotBlank(params.recipientEmail) &&
        noError(params.recipientEmailError) &&
        fieldNotBlank(params.confirmRecipientEmail) &&
        noError(params.confirmRecipientEmailError) &&
        fieldNotBlank(params.nameOnCard) &&
        noError(params.nameOnCardError) &&
        cardComplete
      )
    } else if (!params.mailGift && params.sendToSelf) {
      return (
        fieldNotBlank(params.purchaserName) &&
        noError(params.purchaserNameError) &&
        fieldNotBlank(params.purchaserEmail) &&
        noError(params.purchaserEmailError) &&
        fieldNotBlank(params.confirmPurchaserEmail) &&
        noError(params.confirmPurchaserEmailError) &&
        fieldNotBlank(params.nameOnCard) &&
        noError(params.nameOnCardError) &&
        cardComplete
      )
    } else if (params.mailGift && !params.sendToSelf) {
      return (
        fieldNotBlank(params.purchaserName) &&
        noError(params.purchaserNameError) &&
        fieldNotBlank(params.purchaserEmail) &&
        noError(params.purchaserEmailError) &&
        fieldNotBlank(params.confirmPurchaserEmail) &&
        noError(params.confirmPurchaserEmailError) &&
        fieldNotBlank(params.nameOnCard) &&
        noError(params.nameOnCardError) &&
        fieldNotBlank(recipientName) &&
        noError(recipientNameError) &&
        fieldNotBlank(address1) &&
        noError(address1Error) &&
        fieldNotBlank(city) &&
        noError(cityError) &&
        fieldNotBlank(state) &&
        noError(stateError) &&
        fieldNotBlank(zipCode) &&
        noError(zipCodeError) &&
        fieldNotBlank(cardTo) &&
        noError(cardToError) &&
        fieldNotBlank(cardFrom) &&
        noError(cardFromError) &&
        cardComplete
      )
    } else if (params.mailGift && params.sendToSelf) {
      return (
        fieldNotBlank(params.purchaserName) &&
        noError(params.purchaserNameError) &&
        fieldNotBlank(params.purchaserEmail) &&
        noError(params.purchaserEmailError) &&
        fieldNotBlank(params.confirmPurchaserEmail) &&
        noError(params.confirmPurchaserEmailError) &&
        fieldNotBlank(params.nameOnCard) &&
        noError(params.nameOnCardError) &&
        fieldNotBlank(recipientName) &&
        noError(recipientNameError) &&
        fieldNotBlank(address1) &&
        noError(address1Error) &&
        fieldNotBlank(city) &&
        noError(cityError) &&
        fieldNotBlank(state) &&
        noError(stateError) &&
        fieldNotBlank(zipCode) &&
        noError(zipCodeError) &&
        cardComplete
      )
    }
    return false
  }

  let headerText = ''
  let orderSummaryLengthText = `1 Year of Access`
  let orderSummaryCostText = `${ANNUAL_PRICE_STRING} USD` // full price

  if (props.subscriptionText.plan === SUBSCRIPTION_TYPES.SIX_MONTHS) {
    orderSummaryLengthText = `6 Months of Access`
    orderSummaryCostText = `${SIX_MONTHS_PRICE_STRING} USD` // full price
  }

  if (props.subscriptionText && props.subscriptionText.promoHeaderText) {
    const regularText = props.subscriptionText.promoHeaderText[0] // promo price
    const crossOutText = props.subscriptionText.promoHeaderText[1] // cross out of full price
    headerText = `${regularText} ${crossOutText ? `<span>${crossOutText}</span>` : ``}`
  }

  if (isMailGiftOptionAvailabe) {
    headerText += '. How would you like us to send your gift?'
  }

  let emailGiftMobileImage = DipseaContentfulAssets['gifting-desktop-email_2x.png']
  let emailGiftDesktopImage = DipseaContentfulAssets['gifting-mobile-email_2x.png']
  if (props.featurePrideGift) {
    emailGiftMobileImage = DipseaContentfulAssets.giftEmailPrideMobile
    emailGiftDesktopImage = DipseaContentfulAssets.giftEmailPrideDeskop
  }

  return (
    <Container>
      <Row>
        <Col>
          <HeaderContainer>
            <HeaderText dangerouslySetInnerHTML={{ __html: headerText }}/>
            <SubHeaderText>(😉yeah, seriously)</SubHeaderText>
          </HeaderContainer>
        </Col>
      </Row>
      {isMailGiftOptionAvailabe
        ? (
        <Row>
          <Col
            align='center'
            xs={2}
            sm={4}
            md={3}
            lg={6}
            xl={5}
            offset={{
              xl: 1,
              lg: 0,
              sm: 0,
              md: 1,
              xs: 0
            }}
          >
            <GiftCTA
              isActive={mailGift}
              onClick={(): void => {
                setMailGift(true)
                setSendToSelf(false)
              }}
            >
              By Mail
            </GiftCTA>
          </Col>
          <Col
            align='center'
            xs={2}
            sm={4}
            md={3}
            lg={6}
            xl={5}
            offset={{ lg: 0 }}
          >
            <GiftCTA
              isActive={!mailGift}
              onClick={(): void => {
                setMailGift(false)
                setSendToSelf(false)
              }}
            >
              By Email
            </GiftCTA>
          </Col>
        </Row>
          )
        : null}
      <Row>
        <Col
          align='center'
          xs={4}
          sm={8}
          md={6}
          lg={12}
          xl={10}
          offset={{
            xl: 1,
            lg: 0,
            sm: 0,
            md: 1,
            xs: 0
          }}
        >
          <div style={{ zIndex: 1, width: '100%' }}>
            {mailGift
              ? (
              <MailImgContainer>
                <Hidden xs={true} sm={true} md={true} lg={false} xl={false}>
                  <FadeInImage
                    loader={contentfulLoader}
                    src={DipseaContentfulAssets['gifting-desktop-mail_2x.png']}
                    alt='Dipsea | Gift by Mail'
                    width={2188}
                    height={1134}
                  />
                </Hidden>

                <Hidden xs={false} sm={false} md={false} lg={true} xl={true}>
                  <FadeInImage
                    loader={contentfulLoader}
                    src={DipseaContentfulAssets['gifting-mobile-mail_2x.png']}
                    alt='Dipsea | Gift by Mail'
                    width={932}
                    height={480}
                  />
                </Hidden>
              </MailImgContainer>
                )
              : (
              <MailImgContainer>
                <Hidden xs={true} sm={true} md={true} lg={false} xl={false}>
                  <FadeInImage
                    loader={contentfulLoader}
                    src={emailGiftMobileImage}
                    alt='Dipsea | Gift by Email'
                    width={2188}
                    height={1134}
                  />
                </Hidden>

                <Hidden xs={false} sm={false} md={false} lg={true} xl={true}>
                  <FadeInImage
                    loader={contentfulLoader}
                    src={emailGiftDesktopImage}
                    alt='Dipsea | Gift by Email'
                    width={932}
                    height={480}
                  />
                </Hidden>
              </MailImgContainer>
                )}

            <GiftTextContainer>
              {mailGift
                ? (
                <P18 color={colors.sand} textAlign={'center'}>
                  Dipsea's limited edition signature gift with easy instructions
                  on how to unlock unlimited access to hundreds of sexy stories and
                  wellness sessions. It can be mailed directly to your
                  recipient, or to your doorstep.
                </P18>
                  )
                : (
                <P18 color={colors.sand} textAlign={'center'}>
                  An email with a gift code will be sent directly to your lucky recipient. Want to hand-deliver? You can email to yourself to print at home, too.
                </P18>
                  )}
            </GiftTextContainer>

          </div>
        </Col>
      </Row>
      <Form id='checkoutForm' onSubmit={completeOrder}>
        <Row>
          <Col xl={5} lg={6} xs={4} md={6} offset={{ md: 1, lg: 0, xl: 1 }}>
            <StepBox>
              <P14 fontSize={'14px'} color={colors.sand}>
                STEP 1
              </P14>
              <SectionTitle>
                <H250 color={colors.sand}>Your information</H250>
              </SectionTitle>
              <div>
                <InputContainer>
                  <Input
                    name='purchaserName'
                    placeholder='Your Full Name'
                    aria-label='Your Full Name'
                    autocomplete='name'
                    value={purchaserName}
                    onBlur={(): void => {
                      if (!purchaserName || purchaserName.length === 0) {
                        setPurchaserNameError('Missing your full name')
                      }
                    }}
                    onChange={(event: any): void => {
                      setPurchaserName(event.target.value)
                      setPurchaserNameError(undefined)
                    }}
                    valid={!purchaserNameError}
                  />
                  {purchaserNameError && (
                    <ShowError message={purchaserNameError} />
                  )}
                </InputContainer>

                <InputContainer>
                  <Input
                    name='purchaserEmail'
                    placeholder='Your Email'
                    aria-label='Your Email'
                    autocomplete='email'
                    value={purchaserEmail}
                    onBlur={(): void => {
                      if (!isEmailValid(purchaserEmail)) {
                        setPurchaserEmailError('Your email is not valid')
                      }
                    }}
                    onChange={(event: any): void => {
                      setPurchaserEmail(event.target.value)
                      setPurchaserEmailError(undefined)
                    }}
                    valid={!purchaserEmailError}
                  />
                  {purchaserEmailError && (
                    <ShowError message={purchaserEmailError} />
                  )}
                </InputContainer>

                <InputContainer>
                  <Input
                    name='confirm-your-email'
                    placeholder='Confirm Your Email'
                    aria-label='Confirm Your Email'
                    autocomplete='email'
                    value={confirmPurchaserEmail}
                    onBlur={(): void => {
                      if (
                        !purchaserEmail ||
                        !confirmPurchaserEmail ||
                        purchaserEmail.toLowerCase() !==
                          confirmPurchaserEmail.toLowerCase()
                      ) {
                        setConfirmPurchaserEmailError(
                          'Your emails do not match'
                        )
                      }
                    }}
                    onChange={(event: any): void => {
                      setConfirmPurchaserEmail(event.target.value)
                      setConfirmPurchaserEmailError(undefined)
                    }}
                    valid={!confirmPurchaserEmailError}
                  />
                  {confirmPurchaserEmailError && (
                    <ShowError message={confirmPurchaserEmailError} />
                  )}
                </InputContainer>
              </div>
            </StepBox>
            {mailGift && (
              <StepBox>
                <P14 color={colors.sand}>STEP 2</P14>
                <SectionTitle>
                  <H250 color={colors.sand}>Shipping information</H250>
                </SectionTitle>
                <P14 color={colors.sand} styles={'margin-bottom: 32px;'}>
                  Shipping in the continental US only.
                  <br />
                  Estimated shipping time: 2 weeks.
                </P14>
                <InputContainer>
                  <Input
                    name='recipientName'
                    placeholder="Recipient's Full Name"
                    aria-label="Recipient's Full Name"
                    value={recipientName}
                    onChange={(event: any): void => {
                      setRecipientName(event.target.value)
                      setRecipientNameError(undefined)
                    }}
                    valid={!recipientNameError}
                  />
                  {recipientNameError && (
                    <ShowError message={recipientNameError} />
                  )}
                </InputContainer>
                <InputContainer>
                  <Input
                    name='address1'
                    placeholder='Street Address'
                    aria-label='Street Address'
                    autocomplete='address-line-1'
                    value={address1}
                    onChange={(event: any): void => {
                      setAddress1(event.target.value)
                      setAddress1Error(undefined)
                    }}
                    valid={!address1Error}
                  />
                  {address1Error && <ShowError message={address1Error} />}
                </InputContainer>
                <InputContainer>
                  <Input
                    name='address2'
                    placeholder='Street Address 2'
                    aria-label='Street Address 2'
                    autocomplete='address-line-2'
                    value={address2}
                    onChange={(event: any): void => {
                      setAddress2(event.target.value)
                    }}
                    valid={true}
                  />
                </InputContainer>
                <CityStateZipContainer>
                  <CityContainer>
                    <Input
                      name='city'
                      placeholder='City'
                      aria-label='City'
                      autocomplete='city'
                      value={city}
                      onChange={(event: any): void => {
                        setCity(event.target.value)
                        setCityError(undefined)
                      }}
                      valid={!cityError}
                    />
                    {cityError && <ShowError message={cityError} />}
                  </CityContainer>
                  <StateContainer>
                    <Input
                      name='state'
                      placeholder='State'
                      aria-label='State'
                      autocomplete='address-level1'
                      value={state}
                      onChange={(event: any): void => {
                        setState(event.target.value)
                        setStateError(undefined)
                      }}
                      valid={!stateError}
                    />
                    {stateError && <ShowError message={stateError} />}
                  </StateContainer>
                  <ZipContainer>
                    <Input
                      name='zipCode'
                      placeholder='Zip'
                      aria-label='Zip Code'
                      autocomplete='postal-code'
                      value={zipCode}
                      onChange={(event: any): void => {
                        setZipCode(event.target.value)
                        setZipCodeError(undefined)
                      }}
                      valid={!zipCodeError}
                    />
                    {zipCodeError && <ShowError message={zipCodeError} />}
                  </ZipContainer>
                </CityStateZipContainer>
              </StepBox>
            )}
            {mailGift && (
              <StepBox>
                <P14 color={colors.sand}>STEP 3</P14>
                <H250 color={colors.sand} styles={'margin-top: 8px;'}>
                  Recipient’s information for card
                </H250>
                <CircularCheckBoxContainer>
                  <CheckBox
                    theme={colors.sand}
                    text={'Send to Myself'}
                    onChange={(): void => {
                      setSendToSelf(!sendToSelf)
                      setCardTo(undefined)
                      setCardToError(undefined)
                      setCardFrom(undefined)
                      setCardFromError(undefined)
                    }}
                  />
                </CircularCheckBoxContainer>
                {!sendToSelf && (
                  <>
                    <CardNameContainer>
                      <P14 color={colors.sand} styles={'min-width: 55px;'}>
                        TO:
                      </P14>{' '}
                      <Input
                        name='card-to'
                        placeholder="Recipient's Name"
                        aria-label="Recipient's Name"
                        value={cardTo}
                        onChange={(event: any): void => {
                          setCardTo(event.target.value)
                          setCardToError(undefined)
                        }}
                        valid={!cardToError}
                        styles={'width: 90%; margin-left: 10px;'}
                      />
                      {cardToError && <ShowError message={cardToError} />}
                    </CardNameContainer>
                    <CardNameContainer>
                      <P14 color={colors.sand} styles={'min-width: 55px;'}>
                        FROM:
                      </P14>
                      <Input
                        name='card-from'
                        placeholder='Your Name'
                        aria-label='Your Name'
                        value={cardFrom}
                        onChange={(event: any): void => {
                          setCardFrom(event.target.value)
                          setCardFromError(undefined)
                        }}
                        valid={!cardFromError}
                        styles={'width: 90%; margin-left: 10px;'}
                      />
                      {cardFromError && <ShowError message={cardFromError} />}
                    </CardNameContainer>
                  </>
                )}
              </StepBox>
            )}
            {!mailGift && (
              <StepBox>
                <P14 fontSize={'14px'} color={colors.sand}>
                  STEP 2
                </P14>
                <H250 color={colors.sand} styles={'margin-top: 8px;'}>
                  Recipient's information
                </H250>
                <div>
                  <CircularCheckBoxContainer>
                    <CheckBox
                      text={'Send to Myself'}
                      onChange={(): void => {
                        setSendToSelf(!sendToSelf)
                        setCardTo(undefined)
                        setCardToError(undefined)
                        setCardFrom(undefined)
                        setCardFromError(undefined)
                      }}
                    />
                  </CircularCheckBoxContainer>
                  {!sendToSelf && (
                    <>
                      <InputContainer>
                        <Input
                          name='recipientName'
                          placeholder='Recipient Full Name'
                          aria-label='Recipient Full Name'
                          value={recipientName}
                          onBlur={(): void => {
                            if (!recipientName || recipientName.length === 0) {
                              setPurchaserNameError('Missing recipient name')
                            }
                          }}
                          onChange={(event: any): void => {
                            setRecipientName(event.target.value)
                            setRecipientNameError(undefined)
                          }}
                          valid={!recipientNameError}
                        />
                        {recipientNameError && (
                          <ShowError message={recipientNameError} />
                        )}
                      </InputContainer>

                      <InputContainer>
                        <Input
                          name='recipientEmail'
                          placeholder='Recipient Email'
                          aria-label='Recipient Email'
                          value={recipientEmail}
                          onBlur={(): void => {
                            if (!isEmailValid(recipientEmail)) {
                              setRecipientEmailError('Email is not valid')
                            } else {
                              setRecipientEmailError(undefined)
                            }
                          }}
                          onChange={(event: any): void => {
                            setRecipientEmail(event.target.value)
                            setRecipientEmailError(undefined)
                          }}
                          valid={!recipientEmailError}
                        />
                        {recipientEmailError && (
                          <ShowError message={recipientEmailError} />
                        )}
                      </InputContainer>

                      <InputContainer>
                        <Input
                          name='confirm-recipient-email'
                          placeholder='Confirm Recipient Email'
                          aria-label='Confirm Recipient Email'
                          value={confirmRecipientEmail}
                          onBlur={(): void => {
                            if (
                              !recipientEmail ||
                              !confirmRecipientEmail ||
                              recipientEmail.toLowerCase() !==
                                confirmRecipientEmail.toLowerCase()
                            ) {
                              setConfirmRecipientEmailError(
                                'Emails do not match'
                              )
                            } else {
                              setConfirmRecipientEmailError(undefined)
                            }
                          }}
                          onChange={(event: any): void => {
                            setConfirmRecipientEmail(event.target.value)
                            setConfirmRecipientEmailError(undefined)
                          }}
                          valid={!confirmRecipientEmailError}
                        />
                        {confirmRecipientEmailError && (
                          <ShowError message={confirmRecipientEmailError} />
                        )}
                      </InputContainer>
                    </>
                  )}
                </div>
              </StepBox>
            )}
          </Col>

          <Col xl={5} xs={4} md={6} lg={6} offset={{ md: 1, lg: 0 }}>
            <StepBox>
              <P14 color={colors.sand}>{mailGift ? 'STEP 4' : 'STEP 3'}</P14>
              <SectionTitle>
                <H250 color={colors.sand}>Complete order</H250>
              </SectionTitle>
              <OrderSummaryRow bottomBorder>
                <OrderSummaryBodyText>{orderSummaryLengthText}</OrderSummaryBodyText>
                <OrderSummaryPriceText isStrikethough={!!props.newPrice}>
                  {orderSummaryCostText}
                </OrderSummaryPriceText>
              </OrderSummaryRow>

              {props.newPrice && (
                <OrderSummaryRow bottomBorder>
                  <OrderSummaryBodyText color={colors.tintOrange}>{props.promoText}</OrderSummaryBodyText>
                  <OrderSummaryPriceText color={colors.tintOrange}>
                    {props.newPrice} USD
                  </OrderSummaryPriceText>
                </OrderSummaryRow>
              )}
              <PromoCodeContainer>
                <PromoCode theme={'sand'} isGift={true} />
              </PromoCodeContainer>
              <InputContainer>
                <Input
                  className='checkout-name-on-card'
                  name='name-on-card'
                  placeholder='Name on Credit Card'
                  aria-label='Name on Credit Card'
                  value={nameOnCard}
                  onChange={(event: any): void => {
                    setNameOnCard(event.target.value)
                    setNameOnCardError(undefined)
                  }}
                  onBlur={(_event: any): void => {
                    if (!nameOnCard) {
                      setNameOnCardError('Missing Name')
                    }
                  }}
                  valid={!nameOnCardError}
                />
                {nameOnCardError && <ShowError message={nameOnCardError} />}
              </InputContainer>

              <InputContainer>
                <StripeElementsContainer>
                  <CardElement
                    options={CARD_OPTIONS}
                    onChange={(p: { complete: boolean }): void =>
                      setCardComplete(p.complete)
                    }
                  />
                </StripeElementsContainer>
              </InputContainer>
              <SubmitOrderButton
                disabled={!orderComplete(params)}
                purchasingGift={props.purchasingGift}
              />
              <TermsContainer>
                <TermsText>
                  {`By purchasing a gift, you agree to Dipsea's `}
                  <StyledLink
                    href='/terms'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Terms &amp; Conditions
                  </StyledLink>
                </TermsText>
              </TermsContainer>
              <StoreAvailability color={colors.sand} textAlign={'center'} />
            </StepBox>
          </Col>
        </Row>
      </Form>
    </Container>
  )
}

export default GiftCheckout

const HeaderText = styled(H160)<any>`
  color: ${colors.sand};
  text-align: center;
  span {
    text-decoration: line-through;
    color: ${colors.sand70};
  }
`

const HeaderContainer = styled.div<any>`
  margin-top: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  z-index: 1;

  ${(props: any): any => config(props).media.sm`
    margin-bottom: 16px;
  `}

  ${(props: any): any => config(props).media.lg`
    margin-bottom: 40px;
  `}
`

const SubHeaderText = styled(H160)<any>`
    color: ${colors.sand};
    text-align: center;
    span {
        text-decoration: line-through;
        color: ${colors.sand70};
    }

    font-size: 18px;
    line-height: 36px;
    font-style: italic;
    margin-top: 12px;

    ${(props: any): any => config(props).media.sm`
    font-size: 20px;
    line-height: 20px;
  `}

    ${(props: any): any => config(props).media.lg`
    font-size: 30px;
    line-height: 30px;
  `}

    ${(props: any): any => config(props).media.xl`
    font-size: 40px;
    line-height: 40px;
  `}
`

const GiftCTA = styled.a<any>`
  z-index: 1;
  border-radius: 32px;
  color: ${colors.sand};
  text-transform: uppercase;
  font-size: 12px;
  line-height: 18px;
  padding: 9px 0px 9px 0px;
  font-family: 'Good Sans Bold';
  font-weight: 700;
  background: transparent;
  border: 1px solid ${colors.sand};
  margin-top: 20px;
  width: 100%;
  text-align: center;
  flex-grow: 1;
  cursor: pointer;
  ${(props: { isActive: boolean }): string => {
    if (props.isActive) {
      return `
        background-color: ${colors.orange};
        border: 1px solid ${colors.white};
        color: ${colors.white};

      `
    }
    return ''
  }}
`

const StepBox = styled.div<any>`
  padding: 30px 22px 30px 22px;
  border: 1px solid ${colors.sand};
  border-radius: 10px;
  margin-bottom: 32px;
  ${(props: any): any => config(props).media.sm`
    padding: 30px 22px 30px 22px;
  `}
  z-index: 1;
`

const MailImgContainer = styled.div<any>`
  width: 100%;
  border-radius: 39px;
  margin-top: 32px;
  margin-bottom: 32px;
  img {
    border-radius: 26px;
  }
`

const GiftTextContainer = styled.div<any>`
  margin-bottom: 55px;
`
const InputContainer = styled.div<any>`
  position: relative;
  width: 100%;
  margin-bottom: 24px;
`
const CardNameContainer = styled.div<any>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  align-items: center;
`

const StateContainer = styled.div<any>`
  position: relative;
  width: 25%;
  margin-bottom: 24px;
  margin-right: 16px;
`
const CityContainer = styled.div<any>`
  position: relative;
  width: 50%;
  margin-bottom: 24px;
  margin-right: 16px;
`
const ZipContainer = styled.div<any>`
  position: relative;
  width: 25%;
  margin-bottom: 24px;
`

const ShowError = (props: any): React.ReactNode => (
  <ErrorMessageContainer>
    <ErrorMessage role='alert' creditCard={props.creditCard}>
      {props.message}
    </ErrorMessage>
  </ErrorMessageContainer>
)

const ErrorMessage = styled.p<any>`
  color: ${colors.white};
  font-family: Good Sans Regular;
  font-size: 12px;
  line-height: 12px;
`
const ErrorMessageContainer = styled.div<any>`
  text-align: left;
`

export const Input = styled.input<any>`
  border: 1px solid ${colors.sand};
  border-radius: 8px;
  box-sizing: border-box;
  color: ${colors.sand};
  font-family: "Good Sans Light";
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 150%;
  padding: 10px 0px 10px 12px;
  width: 100%;
  background: transparent;

  ::placeholder {
    color: ${colors.sand};
  }
  &:focus {
    background: ${colors.white};
    outline: none;
    color: ${colors.midnightBlue};
    ::placeholder {
      color: ${colors.midnightBlue};
    }
  }
  ${(props: any): string => {
    if (!props.valid) {
      return `
          border: 1px solid ${colors.orange};
        `
    }
    return ''
  }}

  ${(props: any): string => props.styles || ''}
`

const CityStateZipContainer = styled.div<any>`
  display: flex;
`

const LoadingButtonStyle = styled.div<any>`
  align-items: center;
  background-color: rgba(236, 60, 9, 0.5);
  display: flex;
  height: 40px;
  justify-content: center;
  margin-top: 32px;
  width: 100%;
  border-radius: 32px;

  p {
    color: white;
    font-family: 'Good Sans Regular';
    line-height: 40px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
`

const DisabledButtonStyle = styled.div<any>`
  align-items: center;
  background-color: rgba(236, 60, 9, 0.8);
  display: flex;
  height: 40px;
  justify-content: center;
  margin-top: 32px;
  width: 100%;
  border-radius: 32px;

  .required-text {
    color: red;
    font-family: 'Good Sans Regular';
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  p {
    color: white;
    font-family: 'Good Sans Regular';
    font-size: 12px;
    font-weight: 800;
    line-height: 40px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
`

const SubmitButtonStyle = styled.button<any>`
  border: none;
  color: white;
  background-color: #ec3c09;
  flex: 1;
  font-family: 'Good Sans Regular';
  font-size: 12px;
  font-weight: 800;
  height: 40px;
  letter-spacing: 0.5px;
  margin-top: 32px;
  line-height: 40px;
  text-transform: uppercase;
  width: 100%;
  cursor: pointer;
  outline: none;
  border-radius: 32px;
`
const SubmitOrderButton = (props: any): React.ReactNode => {
  if (props.purchasingGift) {
    return (
      <LoadingButtonStyle>
        <p>PROCESSING</p>
      </LoadingButtonStyle>
    )
  } else if (props.disabled) {
    return (
      <div>
        <DisabledButtonStyle>
          <p>SUBMIT ORDER</p>
        </DisabledButtonStyle>
      </div>
    )
  } else {
    return <SubmitButtonStyle>SUBMIT ORDER</SubmitButtonStyle>
  }
}

const CircularCheckBoxContainer = styled.div<any>`
  display: flex;
  color: ${colors.sand};
  margin-top: 20px;
`

const OrderSummaryBodyText = styled.div<any>`
  flex-grow: 1;
  text-align: left;
  font-family: 'Good Sans Regular';
  font-size: 12px;
  line-height: 18px;
  color: ${(props: { color: string }): string => props.color || colors.sand};

  ${(props: any): any => config(props).media.lg`
    font-size: 14px;
    line-height: 18px;
  `}

   ${(props: any): string =>
     props.isRed && `color: rgb(236,60,9); font-weight: bold;`
  }
`

const OrderSummaryPriceText = styled.div<any>`
  font-family: 'Good Sans Regular';
  font-weight: bold;
  text-align: right;
  min-width: 75px;
  color: ${(props: { color: string }): string => props.color || colors.sand};
  margin: 0;

  font-size: 12px;
  line-height: 18px;

  ${(props: any): any => config(props).media.lg`
     font-size: 14px;
    line-height: 18px;
  `}

  ${(props: any): string =>
    props.isStrikethough &&
    `
    text-decoration: line-through;
  `}
  ${(props: any): string =>
    props.isRed &&
    `
    color: rgb(236,60,9);
  `}
`

const OrderSummaryRow = styled.div<any>`
  display: flex;
  margin-bottom: 20px;
`

const SectionTitle = styled.div<any>`
  margin-top: 8px;
  margin-bottom: 20px;
`
const StripeElementsContainer = styled.div<any>`
  div.StripeElement {
    font-family: "Good Sans Light";
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 150%;
    border: 1px solid ${colors.sand};
    border-radius: 7px;
    padding: 10px 0 10px 12px;
    overflow: hidden;
  }

  div.StripeElement--focus {
    background-color: ${colors.white};
  }
  .StripeElement--invalid {
    background-color: ${colors.white};
  }
  .StripeElement--webkit-autofill {
    background-color: ${colors.white};
  }
`
const PromoCodeContainer = styled.div<any>`
  margin: 32px 0;
`

const TermsContainer = styled.div<any>`
  margin: 32px 0;
`

const TermsText = styled.p<any>`
  color: ${colors.sand};
  font-family: 'Good Sans Regular';
  font-size: 14px;
  letter-spacing: 0;
  line-height: 21px;
  text-align: center;
`

const StyledLink = styled.a<any>`
  color: ${colors.sand};
  font-family: 'Good Sans Regular';
  font-size: 14px;
  letter-spacing: 0;
  line-height: 21px;
  text-align: center;
  font-weight: bold;
  text-decoration: underline;
  border: 1px solid :read-only;

  :link {
    color: ${colors.sand};
  }
  :visited {
    color: ${colors.sand};
  }
  :hover {
    color: ${colors.sand};
  }
  :active {
    color: ${colors.sand};
  }

`
